import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, DatePipe, NgClass } from '@angular/common';
import { DIALOG_DATA, DialogRef, DialogService, ForgeBadgeModule, ForgeButtonModule, ForgeCardModule, ForgeDialogModule, ForgeDividerModule, ForgeIconButtonModule, ForgeIconModule, ForgeScaffoldModule, ForgeTabBarModule, ForgeToolbarModule, ForgeViewSwitcherModule, IDialogOptions } from '@tylertech/forge-angular';
import { Subscription } from 'rxjs';
import { SharedModule } from 'src/app/modules/shared/shared.module';
import { ChargeDetailsComponent } from './charge-details/charge-details.component';
import { BondDetailsComponent, BondSetting } from './bond-details/bond-details.component';
import { Charge, ImageDetails, Inmate, InmatesService } from "@tylertech/cj-inmate-search-angular-sdk";
import { InstanceConfigService } from 'src/app/core/services/instance-config.service';
import { toMugshotDetails } from 'src/app/utils/image.utils';
import { formatAliases } from 'src/app/utils/format-aliases';
import { EnlargedMugshotModalComponent } from 'src/app/components/enlarged-mugshot-modal/enlarged-mugshot-modal.component';

@Component({
  selector: 'inmate-details',
  standalone: true,
  templateUrl: './inmate-details.component.html',
  styleUrls: ['./inmate-details.component.scss'],
  imports: [
    ForgeDialogModule,
    ForgeScaffoldModule,
    ForgeToolbarModule,
    ForgeIconButtonModule,
    ForgeIconModule,
    ForgeButtonModule,
    ForgeTabBarModule,
    ForgeViewSwitcherModule,
    ForgeBadgeModule,
    ForgeCardModule,
    ForgeDividerModule,
    SharedModule,
    DatePipe,
    NgClass,
    ChargeDetailsComponent,
    BondDetailsComponent,
    CommonModule
  ]
})
export class InmateDetailsComponent implements OnInit, OnDestroy {
  inmate!: Inmate;
  charges: Charge[];
  bondSettings: BondSetting[];
  bondTotalAmount: number;
  bondFeesTotal: number;
  totalAmountDue: number;
  hasBondFees: boolean;
  activeTabIndex = 0;
  protected mugshotImageDetails: ImageDetails | null = null;

  hideDateOfBirth = false;
  hideAddress = false;
  hideAlias = false;
  hideMugShots = false;
  displayBondFees = false;
  private _vineUrl: string | null = null;
  private readonly _subscriptions: Subscription[] = [];

  constructor(
    @Inject(DIALOG_DATA) public data: any,
    private readonly _dialogRef: DialogRef,
    private readonly _inmateService: InmatesService,
    private readonly _instanceConfigService: InstanceConfigService,
    private readonly _dialogService: DialogService
  ) {
    this.inmate = data.inmate;
    this.charges = this.inmate.arrests?.flatMap((arrest) => arrest.charges) ?? [];

    // Extract bond settings from inmate data
    this.bondSettings = (this.inmate as any).bondSettings as BondSetting[] || [];

    // Calculate bond totals from individual bonds
    this.bondTotalAmount = this.inmate.bonds?.reduce((total, bond) => total + (bond.amount ?? 0), 0) ?? 0;

    // Calculate bond fees total
    this.bondFeesTotal = this.bondSettings.reduce((total, setting) => {
      const settingFeesSum = setting.bondFees?.reduce((sum, fee) => sum + (fee.amount ?? 0), 0) ?? 0;
      return total + settingFeesSum;
    }, 0);

    // Calculate total amount due
    this.totalAmountDue = this.bondTotalAmount + this.bondFeesTotal;

    // Check if any bond setting has fees
    this.hasBondFees = this.bondSettings.some(setting => setting.bondFees && setting.bondFees.length > 0);
  }

  // Check if we should show "contact facility" message in summary
  // Only show this if we can't determine fees for ANY bond setting
  get shouldShowContactFacilityInSummary(): boolean {
    return this.bondSettings.length > 0 && this.bondSettings.every(setting => !setting.canDetermineBondFees);
  }

  ngOnInit(): void {
    const visibility = this._instanceConfigService.inmateVisibilitySnapshot;

    if (visibility) {
      this.hideDateOfBirth = visibility.hideDob ?? false;
      this.hideAddress = visibility.hideAddress ?? false;
      this.hideAlias = visibility.hideAlias ?? false;
      this.hideMugShots = visibility.hideMugShots ?? false;
      this.displayBondFees = visibility.displayBondFees ?? false;
    } else {
      console.warn('Inmate visibility config not yet loaded');
    }

    if (!this.hideMugShots) {
      this.loadMugshot();
    }

    this._subscriptions.push(
      this._instanceConfigService.branding$.subscribe(branding => {
        const url = branding?.vineUrl;
        this._vineUrl = url?.trim() || null;
      })
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  closeDialog(): void {
    this._dialogRef.close(null);
  }

  openEnlargedMugshot(): void {
    if (this.hideMugShots) return;

    const imageUrl = this.mugshotImageDetails?.originalUrl
      ?? this.mugshotImageDetails?.imageUrls?.size800
      ?? this.mugshotImageDetails?.imageUrls?.size520
      ?? this.mugshotImageDetails?.imageUrls?.size320
      ?? 'assets/no-image-headshot.png';

    const title = this.inmate?.name?.fullName || 'Inmate mugshot';

    const options: IDialogOptions = {
      label: title,
      description: 'Enlarged inmate mugshot image',
      attributes: new Map([
        ['style', '--forge-dialog-width: 1024px; --forge-dialog-height: 768px; --forge-dialog-max-width: 90vw; --forge-dialog-max-height: 90vh;']
      ])
    };

    this._dialogService.open(EnlargedMugshotModalComponent, {
      options,
      data: {
        imageUrl,
        inmateName: title
      }
    });
  }

  private loadMugshot() {
    const isDoc = (this.inmate as any)?.recordType === 'DOC';
    if (!this.inmate.imageServiceClientId && !isDoc) {
      this.mugshotImageDetails = null;
      return;
    }
    this._inmateService.getInmateImage(this.inmate.instanceId, this.inmate.id, this.inmate.imageServiceClientId).subscribe({
      next: (imageDetails) => {
        this.mugshotImageDetails = toMugshotDetails(imageDetails);
      },
      error: (err) => {
        this.mugshotImageDetails = null;
        if (err.status === 404) return;
        console.error(err);
      }
    });
  }

  protected onImageError(): void {
    this.mugshotImageDetails = null;
  }

  get vineUrl(): string | null {
    if (this._vineUrl) return this._vineUrl;
    const url = this._instanceConfigService.brandingSnapshot?.vineUrl;
    return url?.trim() || null;
  }

  openVineUrl(): void {
    const baseUrl = this.vineUrl?.trim();
    if (!baseUrl) return;

    const offenderId = (this.inmate as any).offenderID || (this.inmate as any).pid || (this.inmate as any).docNumber || this.inmate.soNumber || this.inmate.id;
    if (!offenderId) return;

    try {
      const url = new URL(baseUrl);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return;

      url.searchParams.set('offenderID', String(offenderId));
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } catch {
      // Invalid URL; ignore
    }
  }

  onTabChange(event: CustomEvent): void {
    this.activeTabIndex = (event as CustomEvent<{ index: number }>).detail.index;
  }

  get formattedAliases(): string {
    return formatAliases(this.inmate?.aliases);
  }

  // Booking facility name (BookingLocationName from the JM export); shown as the Facility value, "n/a" when absent.
  get facilityName(): string | undefined {
    return this.inmate?.BookingLocationName;
  }

  // True when any hold on this inmate is currently Active; drives the Holds Yes/No indicator.
  get hasActiveHold(): boolean {
    return this.inmate.Holds?.some(
      (hold) => hold?.HoldStatus?.BaseStatus === 'Active'
    ) ?? false;
  }

  // Holds indicator for display: "n/a" when no holds data is present, otherwise "Yes"/"No".
  get holdsLabel(): string {
    const holds = (this.inmate as { Holds?: unknown[] }).Holds;
    if (!holds || holds.length === 0) {
      return 'n/a';
    }
    return this.hasActiveHold ? 'Yes' : 'No';
  }
}